import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {Toast} from "primereact/toast";
import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";

import {formatValue} from "@reva/shared/number-format";

import {useLanguage, useRevaTranslation} from "../../hooks/languageHooks";
import {selectAssociatedOutputParamOutputCurried} from "../../redux/objectParametersSelectors";
import {DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL} from "../../redux/pdfConstants";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {snakeToCamel} from "../../util/stringUtil";
import ParameterInputArrayDisabled from "./ParameterInputArrayDisabled";
import {getResourceColor, useInputParameterDef, useParameterValue} from "./paramHooks";
import {ROW_ACTION_COLUMN_STYLE, RowDeleteIcon} from "./RowDeleteIcon";

export const PARAM_GROSS_FLOOR_AREA = "gross_floor_area";
export {DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL};

function GrossFloorAreaFinalValueInput({parameterType, className, disabled}) {
    const {t, language} = useRevaTranslation();

    const toastRef = useRef(null);

    const {parameterValues: grossFloorAreaValues, onValueChange: setGrossFloorAreaValue} = useParameterValue(
        PARAM_GROSS_FLOOR_AREA,
        parameterType,
    );

    const {parameterValues: modelValues, onValueChange: setModelValue} = useParameterValue(
        DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL,
        "DOC_PROPERTY",
    );

    const associatedOutputParamValue = useSelector(selectAssociatedOutputParamOutputCurried(PARAM_GROSS_FLOOR_AREA));
    const hasAssociatedOutput = typeof associatedOutputParamValue === "number";

    const parameterData = useInputParameterDef(PARAM_GROSS_FLOOR_AREA);
    const id = snakeToCamel(`input_${PARAM_GROSS_FLOOR_AREA}`);

    const fmtNumber = (value) => formatValue(value, language);

    const hasConflict = false; // TODO
    const border = getResourceColor("reva", hasConflict);
    const filledPlaceholderClass = hasAssociatedOutput ? "indication-filled-placeholder" : "";

    const placeholder = hasAssociatedOutput
        ? `${t("object.associatedOutputParamPrefix")} ${fmtNumber(associatedOutputParamValue)}`
        : parameterData?.maxValue
          ? `max. ${fmtNumber(parameterData.maxValue)}`
          : null;

    const onChange = (e) => {
        if (e.originalEvent && !disabled) {
            // Reset Model (only after manual change)
            if (modelValues?.activeParameterValue?.items?.length && toastRef.current) {
                toastRef.current.show({
                    severity: "warn",
                    summary: "Bruttogrundfläche überschrieben",
                    detail: "Die Eingabetabelle für die Bruttogrundfläche wurde zurückgesetzt, da eine expliziter Wert eingegeben wurde",
                    life: 10000,
                });
            }
            setModelValue(null, null, true);
            setGrossFloorAreaValue(e.value, grossFloorAreaValues.comment);
        }
    };

    return (
        <>
            <Toast ref={toastRef} />
            <InputNumber
                inputId={id}
                className={className}
                inputClassName={`text-right border-${border} ${filledPlaceholderClass}`}
                inputStyle={{width: "8rem"}}
                value={grossFloorAreaValues.activeParameterValue}
                locale={language}
                placeholder={placeholder}
                useGrouping={true}
                minFractionDigits={2}
                maxFractionDigits={2}
                suffix=" m²"
                disabled={disabled}
                onValueChange={onChange}
            />
        </>
    );
}

function GrossFloorAreaFooter({parameterType, disabled}) {
    return (
        <div className="font-bold w-full flex flex-row items-center inputtext-compact">
            <span className="text-right flex-1 mr-4">&Sigma;</span>
            <GrossFloorAreaFinalValueInput className="" disabled={disabled} parameterType={parameterType} />
        </div>
    );
}

/**
 * @typedef {object} InputGrossFloorAreaModel
 * @property {number|null} computedValue
 * @property {boolean|null} valid
 * @property {InputGrossFloorAreaModelItem[]} items
 */

/**
 * @typedef {object} InputGrossFloorAreaModelItem
 * @property {string|null} description
 * @property {number|null} factor
 * @property {number|null} length
 * @property {number|null} width
 * @property {number|null} space
 * @property {number|null} gross_floor_area
 */

/**
 * @typedef {InputGrossFloorAreaModelItem} InputGrossFloorAreaRow
 * @property {boolean|null} newRow
 * @property {boolean|null} factorInvalid
 * @property {boolean|null} lengthInvalid
 * @property {boolean|null} widthInvalid
 * @property {boolean|null} spaceInvalid
 * @property {boolean|null} spaceEntered
 * @property {boolean} disabled
 */

/**
 * Compute all dependent values for the Gross Floor Area, and change the values in the model.
 * @param {InputGrossFloorAreaModel} model
 * @param {number|undefined} [rowIndex] last changed row index
 * @param {string|undefined} [field] last changed field
 */
function computeGrossFloorAreaValues(model, rowIndex, field) {
    if (typeof rowIndex === "number" && typeof field === "string") {
        const row = model.items[rowIndex];
        // recalculate space from length and width
        if (field === "length" || field === "width") {
            if (isLengthOrWidthInvalid(row.length) || isLengthOrWidthInvalid(row.width)) {
                delete row.space;
            } else {
                row.space = Math.round(row.length * row.width * 100) / 100;
            }
        }
        // reset length/width from explicit space input
        if (field === "space") {
            delete row.length;
            delete row.width;
        }
        // calculate BGF
        if (field === "length" || field === "width" || field === "space" || field === "factor") {
            if (isSpaceInvalid(row.space) || isFactorInvalid(row.factor)) {
                delete row.gross_floor_area;
            } else {
                row.gross_floor_area = Math.round(row.space * row.factor * 100) / 100;
            }
        }
    }

    // Calculate total
    let valid = true;
    let total = null;
    for (const item of model.items) {
        if (typeof item.gross_floor_area === "number" && !Number.isNaN(item.gross_floor_area)) {
            total = (total || 0) + item.gross_floor_area;
        } else {
            valid = false;
        }
    }
    model.valid = valid;
    model.computedValue = total;
}

const isFactorInvalid = (factor) => !factor || factor > 100;
const isLengthOrWidthInvalid = (size) => !size || size < 0.1 || size > 400;
const isSpaceInvalid = (space) => !space || space < 0.01 || space > 160000;

/**
 * Convert model items to data table rows.
 * @param {InputGrossFloorAreaModel} model
 * @param {number} lastUpdate last update timestamp in rows, to force to rerender even unchanged rows
 * @param {boolean} disabled flag, if input is disabled (e.g. valuation is finalized) and no new input is possible
 * @return {InputGrossFloorAreaRow[]} converted row items
 */
function convertModelToRows(model, lastUpdate, disabled) {
    let index = 0;
    const convertedRows = model.items.map((item) => {
        return {
            ...item,
            index: index++,
            factorInvalid: isFactorInvalid(item.factor),
            lengthInvalid: isLengthOrWidthInvalid(item.length),
            widthInvalid: isLengthOrWidthInvalid(item.width),
            spaceInvalid: isSpaceInvalid(item.space),
            spaceEntered: item.space && !item.length && !item.width,
            updated: lastUpdate,
            disabled: disabled,
        };
    });
    if (disabled) {
        return convertedRows;
    }
    const newRow = {
        newRow: true,
        index,
        updated: lastUpdate,
    };
    return [...convertedRows, newRow];
}

/**
 * Input cell for column "description".
 * @param {InputGrossFloorAreaRow} rowData
 * @param {(string) => void} onChange
 * @return {React.ReactNode}
 */
export function CellInputDescription({rowData, onChange}) {
    const value = rowData.description || "";

    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    return (
        <InputText
            value={localValue}
            className="w-full"
            disabled={rowData.disabled}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={(e) => {
                if (e.target.value === value) {
                    return;
                }
                onChange(e.target.value);
            }}
        />
    );
}

/**
 * Input cell for column "factor".
 * @param {InputGrossFloorAreaRow} rowData
 * @param {(number) => void} onChange
 * @return {React.ReactNode}
 */
export function CellInputFactor({rowData, onChange}) {
    const language = useLanguage();
    const currentValue = rowData.factor || null;
    const isInvalid = !!rowData.factorInvalid;
    return (
        <InputNumber
            value={currentValue}
            minFractionDigits={2}
            maxFractionDigits={2}
            inputClassName="text-right w-full"
            onValueChange={(e) => {
                if (e.originalEvent) {
                    onChange(e.value);
                }
            }}
            invalid={isInvalid}
            disabled={rowData.disabled}
            locale={language}
        />
    );
}

/**
 * Input cell for column "length" or "width".
 * @param {InputGrossFloorAreaRow} rowData
 * @param {"length"|"width"} field
 * @param {(number) => void} onChange
 * @return {React.ReactNode}
 */
export function CellInputLengthOrWidth({rowData, field, onChange}) {
    const language = useLanguage();
    const currentValue = rowData[field] || null;
    const isDisabled = !!rowData.spaceEntered || rowData.disabled;
    const isInvalid = !isDisabled && rowData[`${field}Invalid`];
    return (
        <InputNumber
            value={currentValue}
            minFractionDigits={2}
            maxFractionDigits={2}
            inputClassName="text-right w-full"
            suffix=" m"
            onValueChange={(e) => {
                if (e.originalEvent) {
                    onChange(e.value);
                }
            }}
            disabled={isDisabled}
            invalid={isInvalid}
            locale={language}
        />
    );
}

/**
 * Input cell for column "space".
 * @param {InputGrossFloorAreaRow} rowData
 * @param {(number) => void} onChange
 * @return {React.ReactNode}
 */
export function CellInputSpace({rowData, onChange}) {
    const language = useLanguage();
    const currentValue = rowData.space || null;
    const isDisabled = !!rowData.width || !!rowData.length || rowData.disabled;
    const isInvalid = !isDisabled && rowData.spaceInvalid;
    return (
        <InputNumber
            value={currentValue}
            minFractionDigits={2}
            maxFractionDigits={2}
            inputClassName="text-right w-full"
            suffix=" m²"
            onValueChange={(e) => {
                if (e.originalEvent) {
                    onChange(e.value);
                }
            }}
            invalid={isInvalid}
            disabled={isDisabled}
            locale={language}
        />
    );
}

/**
 * Input cell for column "gross floor area".
 * @param {InputGrossFloorAreaRow} rowData
 * @return {React.ReactNode}
 */
export function CellInputGFA({rowData}) {
    const language = useLanguage();
    const currentValue = rowData.gross_floor_area || null;
    return (
        <InputNumber
            value={currentValue}
            minFractionDigits={2}
            maxFractionDigits={2}
            inputClassName="text-right w-full"
            suffix=" m²"
            disabled={true}
            locale={language}
        />
    );
}

/**
 * Aktions-Icon einer Eingabezeile: nur bestehende Zeilen bekommen das klickbare Minus zum Löschen.
 * Die stets leere Neu-Zeile bleibt ohne Icon – ein Plus hätte dort keine Funktion.
 * @param {object} props
 * @param {{newRow?: boolean}} props.rowData
 * @param {() => void} props.onDelete
 * @return {React.ReactNode}
 */
export function CellInputActionIcon({rowData, onDelete}) {
    if (rowData.newRow) {
        return null;
    }
    return <RowDeleteIcon onDelete={onDelete} />;
}

/**
 *
 * @param {RevaValueParameterType} parameterType
 * @returns {React.ReactNode}
 */
export function InputGrossFloorAreaEditPanel({parameterType = "AVM"}) {
    const {parameterValues: grossFloorAreaValues, onValueChange: setGrossFloorAreaValue} = useParameterValue(
        PARAM_GROSS_FLOOR_AREA,
        parameterType,
    );

    const {parameterValues: modelValues, onValueChange: setModelValue} = useParameterValue(
        DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL,
        "DOC_PROPERTY",
    );

    const disabled = useSelector(selectReadOnly);

    const [lastUpdate, setLastUpdate] = useState(Date.now);

    /** @type {InputGrossFloorAreaModel} */
    const effectiveModel = modelValues.activeParameterValue || {items: []};
    const rows = convertModelToRows(effectiveModel, lastUpdate, disabled);

    /**
     * Change a value in a row.
     * @param {number} rowIndex index of the row
     * @param {string} field field to change
     * @param value new value
     */
    const onRowChange = (rowIndex, field, value) => {
        const newModel = structuredClone(effectiveModel);
        const items = newModel.items;
        if (rowIndex < 0 || rowIndex > items.length) {
            console.warn("Invalid row Index", rowIndex);
            return;
        }
        if (rowIndex === items.length) {
            if (!value) {
                return;
            }
            // Add new row
            items[rowIndex] = {factor: 1.0};
        }
        items[rowIndex][field] = value;

        // Check if line should be removed
        if (
            field === "X" ||
            (!items[rowIndex].description &&
                !items[rowIndex].factor &&
                !items[rowIndex].length &&
                !items[rowIndex].width &&
                !items[rowIndex].space)
        ) {
            items.splice(rowIndex, 1);
            // Compute changes of total
            computeGrossFloorAreaValues(newModel);
        } else {
            // Compute changes of edited line and of total
            computeGrossFloorAreaValues(newModel, rowIndex, field);
        }

        setLastUpdate(Date.now());
        setModelValue(newModel, null, true);
        const computedValue = newModel.computedValue;
        setGrossFloorAreaValue(computedValue, computedValue ? grossFloorAreaValues.comment : null);
    };

    const onRowReorder = (reorderedRows) => {
        const newModel = structuredClone(effectiveModel);
        newModel.items = reorderedRows;
        const newRow = reorderedRows.findIndex((row) => row.newRow);
        newModel.items.splice(newRow, 1);
        setModelValue(newModel, null, true);
    };

    return (
        <DataTable
            className="p-inputtext-sm text-sm inputtext-compact"
            value={rows}
            dataKey="index"
            footer={<GrossFloorAreaFooter parameterType={parameterType} disabled={disabled} />}
            pt={{headerRow: {className: "font-normal"}}}
            size="small"
            reorderableRows
            onRowReorder={(e) => onRowReorder(e.value)}
        >
            <Column rowReorder className="w-8" />
            {!disabled && (
                <Column
                    style={ROW_ACTION_COLUMN_STYLE}
                    body={(rowData, {rowIndex}) => (
                        <CellInputActionIcon rowData={rowData} onDelete={() => onRowChange(rowIndex, "X", null)} />
                    )}
                />
            )}
            <Column
                header="Beschreibung"
                field="description"
                body={(rowData, {rowIndex, field}) => (
                    <CellInputDescription rowData={rowData} onChange={(value) => onRowChange(rowIndex, field, value)} />
                )}
            />
            <Column
                header="Faktor"
                field="factor"
                alignHeader="right"
                headerStyle={{width: "5rem"}}
                body={(rowData, {rowIndex, field}) => (
                    <CellInputFactor rowData={rowData} onChange={(value) => onRowChange(rowIndex, field, value)} />
                )}
            />
            <Column
                header="Länge"
                field="length"
                alignHeader="right"
                headerStyle={{width: "6rem"}}
                body={(rowData, {rowIndex, field}) => (
                    <CellInputLengthOrWidth
                        field={field}
                        rowData={rowData}
                        onChange={(value) => onRowChange(rowIndex, field, value)}
                    />
                )}
            />
            <Column
                header="Breite"
                field="width"
                alignHeader="right"
                headerStyle={{width: "6rem"}}
                body={(rowData, {rowIndex, field}) => (
                    <CellInputLengthOrWidth
                        field={field}
                        rowData={rowData}
                        onChange={(value) => onRowChange(rowIndex, field, value)}
                    />
                )}
            />
            <Column
                header="Fläche"
                field="space"
                alignHeader="right"
                headerStyle={{width: "8rem"}}
                body={(rowData, {rowIndex, field}) => (
                    <CellInputSpace rowData={rowData} onChange={(value) => onRowChange(rowIndex, field, value)} />
                )}
            />
            <Column
                header="BGF"
                field="gross_floor_area"
                alignHeader="right"
                headerStyle={{width: "9rem"}}
                body={(rowData) => <CellInputGFA rowData={rowData} />}
            />
        </DataTable>
    );
}

export function ParameterInputGrossFloorArea({
    parameter,
    parameterType,
    label,
    id,
    parameter_details_def,
    associatedOutputParam,
    originTab,
}) {
    return (
        <ParameterInputArrayDisabled
            parameter={parameter}
            parameterType={parameterType}
            label={label}
            id={id}
            parameter_details_def={parameter_details_def}
            associatedOutputParam={associatedOutputParam}
            originTab={originTab}
        />
    );
}
